<script setup>
import { computed, ref, onMounted, watch } from 'vue';
import { usePortalAccount } from '../../composables/usePortalAccount';
import axios from 'axios';
import Auth from 'dashboard/api/auth';
import ConversationsListWidget from './ConversationsListWidget.vue';

const props = defineProps({
  widget: {
    type: Object,
    required: true,
  },
});

const { accountId } = usePortalAccount();
const inboxes = ref([]);
const loading = ref(true);
const error = ref(null);
const selectedInbox = ref(null);

const getAuthHeaders = () => {
  const headers = {
    'Content-Type': 'application/json',
  };

  if (Auth.hasAuthCookie()) {
    const authData = Auth.getAuthData();
    if (authData && authData['access-token']) {
      headers['access-token'] = authData['access-token'];
      headers['token-type'] = authData['token-type'];
      headers.client = authData.client;
      headers.expiry = authData.expiry;
      headers.uid = authData.uid;
    }
  }

  return headers;
};

const authorizedInboxIds = computed(() => props.widget.props?.inboxIds || []);
const channelTypeFilter = computed(
  () => props.widget.props?.channelType || null
);
const contactId = computed(() => props.widget.props?.contactId || null);

const fetchInboxes = async () => {
  try {
    const response = await axios.get(
      `/api/v1/accounts/${accountId.value}/inboxes`,
      {
        headers: getAuthHeaders(),
      }
    );
    const allInboxes = response.data?.payload || response.data || [];

    let filteredInboxes = allInboxes;

    // Filtrer par type de canal si spécifié (ex: 'whatsapp')
    if (channelTypeFilter.value) {
      const channelTypeMap = {
        whatsapp: 'Channel::Whatsapp',
        email: 'Channel::Email',
        webwidget: 'Channel::WebWidget',
        sms: 'Channel::Sms',
        api: 'Channel::Api',
      };
      const targetChannelType =
        channelTypeMap[channelTypeFilter.value.toLowerCase()];
      if (targetChannelType) {
        filteredInboxes = filteredInboxes.filter(
          inbox => inbox.channel_type === targetChannelType
        );
      }
    }

    // Filtrer par inboxes autorisées
    if (authorizedInboxIds.value.length > 0) {
      filteredInboxes = filteredInboxes.filter(inbox =>
        authorizedInboxIds.value.includes(inbox.id)
      );
    }

    inboxes.value = filteredInboxes;

    // Si une seule inbox WhatsApp et un contactId est fourni, sélectionner automatiquement
    if (
      channelTypeFilter.value?.toLowerCase() === 'whatsapp' &&
      inboxes.value.length === 1 &&
      contactId.value
    ) {
      selectedInbox.value = inboxes.value[0];
    }
  } catch (err) {
    error.value = 'Erreur lors du chargement des inboxes';
  } finally {
    loading.value = false;
  }
};

onMounted(() => {
  fetchInboxes();
});

const selectedContact = ref(null);
const contacts = ref([]);
const contactsLoading = ref(false);

const fetchContacts = async () => {
  if (!selectedInbox.value) return;
  contactsLoading.value = true;
  try {
    const response = await axios.get(
      `/api/v1/accounts/${accountId.value}/contacts`,
      {
        headers: getAuthHeaders(),
      }
    );
    contacts.value = response.data?.payload || response.data || [];

    // Si un contactId est fourni dans les props, sélectionner automatiquement ce contact
    if (contactId.value) {
      const foundContact = contacts.value.find(c => c.id === contactId.value);
      if (foundContact) {
        selectedContact.value = foundContact;
      }
    }
  } catch (err) {
    // Erreur silencieuse - l'utilisateur verra un état vide
  } finally {
    contactsLoading.value = false;
  }
};

const selectInbox = inbox => {
  selectedInbox.value = inbox;
  selectedContact.value = null; // Réinitialiser le contact lors du changement d'inbox
};

const selectContact = contact => {
  selectedContact.value = contact;
};

// État pour le mode "Nouveau Message"
const isNewMessageMode = ref(false);
const newMessageContent = ref('');
const isCreatingConversation = ref(false);

const startNewMessage = () => {
  isNewMessageMode.value = true;
};

const cancelNewMessage = () => {
  isNewMessageMode.value = false;
  newMessageContent.value = '';
};

const createConversationAndSendMessage = async () => {
  if (!newMessageContent.value.trim() || isCreatingConversation.value) return;

  isCreatingConversation.value = true;
  try {
    // 1. Créer la conversation
    const convResponse = await axios.post(
      `/api/v1/accounts/${accountId.value}/conversations`,
      {
        source_id: `client_portal_${Date.now()}`,
        contact_id: selectedContact.value.id,
        inbox_id: selectedInbox.value.id,
        status: 'open',
      },
      {
        headers: getAuthHeaders(),
      }
    );

    const conversation = convResponse.data;

    // 2. Envoyer le message
    await axios.post(
      `/api/v1/accounts/${accountId.value}/conversations/${conversation.id}/messages`,
      {
        content: newMessageContent.value,
        message_type: 'incoming', // Message envoyé par le client
      },
      {
        headers: getAuthHeaders(),
      }
    );

    // 3. Recharger la liste et passer à la vue conversation
    isNewMessageMode.value = false;
    newMessageContent.value = '';
    // On laisse le ConversationsListWidget gérer la suite
  } catch (err) {
    // Erreur silencieuse - l'utilisateur peut réessayer
  } finally {
    isCreatingConversation.value = false;
  }
};

watch(selectedInbox, newInbox => {
  if (newInbox) {
    fetchContacts();
  }
});

// Si le widget est configuré pour WhatsApp avec un contactId, charger automatiquement
watch(
  () => [channelTypeFilter.value, contactId.value, loading.value],
  ([channelType, contact, isLoading]) => {
    if (
      !isLoading &&
      channelType?.toLowerCase() === 'whatsapp' &&
      contact &&
      selectedInbox.value &&
      !selectedContact.value
    ) {
      fetchContacts();
    }
  },
  { immediate: true }
);

const goBackToInboxes = () => {
  selectedInbox.value = null;
  selectedContact.value = null;
};

const goBackToContacts = () => {
  selectedContact.value = null;
  isNewMessageMode.value = false;
};
</script>

<template>
  <div
    class="bg-white rounded-2xl p-5 border border-slate-100 flex flex-col relative overflow-hidden transition-all hover:border-slate-300 h-full shadow-sm"
  >
    <div
      class="flex items-center justify-between mb-4 relative z-10 border-b border-slate-50 pb-2"
    >
      <div class="flex items-center gap-2">
        <div
          class="w-6 h-6 rounded-full flex items-center justify-center"
          style="background-color: #10b981"
        >
          <span
            style="
              color: #ffffff;
              font-size: 9px;
              font-weight: bold;
              line-height: 1;
            "
            >AI</span>
        </div>
        <h3 class="text-sm font-semibold text-slate-700">
          <template v-if="selectedContact">
            Chat avec {{ selectedContact.name }}
          </template>
          <template v-else-if="selectedInbox">
            {{ selectedInbox.name }}
          </template>
          <template v-else>
            AI Assistant
            <span class="text-slate-400 font-normal">4o</span>
          </template>
        </h3>
      </div>
      <button class="text-slate-400 hover:text-slate-600 transition-colors">
        <span class="i-lucide-more-horizontal text-base" />
      </button>
    </div>

    <!-- Chargement -->
    <div v-if="loading" class="flex-1 flex items-center justify-center py-20">
      <div
        class="w-10 h-10 border-2 border-slate-100 border-t-emerald-500 rounded-full animate-spin"
      />
    </div>

    <!-- Erreur -->
    <div
      v-else-if="error"
      class="flex-1 flex flex-col items-center justify-center p-8 text-center"
    >
      <span class="i-lucide-alert-circle text-3xl text-rose-400 mb-3" />
      <p class="text-xs font-bold text-slate-600">{{ error }}</p>
    </div>

    <!-- Liste des Inboxes -->
    <div
      v-else-if="!selectedInbox"
      class="flex-1 space-y-3 overflow-y-auto custom-scrollbar pr-1"
    >
      <div
        v-for="inbox in inboxes"
        :key="inbox.id"
        class="group/item flex items-center justify-between p-3 hover:bg-slate-50 rounded-xl transition-all duration-300 cursor-pointer border border-slate-100 hover:border-slate-200"
        @click="selectInbox(inbox)"
      >
        <div class="flex items-center gap-3 flex-1 min-w-0">
          <div
            class="w-10 h-10 rounded-lg bg-slate-100 border border-slate-200 flex items-center justify-center"
          >
            <span
              v-if="inbox.channel_type === 'Channel::WebWidget'"
              class="i-lucide-globe text-slate-600 text-lg"
            />
            <span
              v-else-if="inbox.channel_type === 'Channel::Email'"
              class="i-lucide-mail text-slate-600 text-lg"
            />
            <span
              v-else-if="inbox.channel_type === 'Channel::Whatsapp'"
              class="i-lucide-phone text-slate-600 text-lg"
            />
            <span v-else class="i-lucide-inbox text-slate-600 text-lg" />
          </div>
          <div class="min-w-0">
            <p
              class="text-sm font-semibold text-slate-900 tracking-tight truncate"
            >
              {{ inbox.name }}
            </p>
            <p
              class="text-[10px] font-medium text-slate-400 uppercase tracking-wide"
            >
              {{ inbox.channel_type.replace('Channel::', '') }}
            </p>
          </div>
        </div>
        <span
          class="i-lucide-chevron-right text-slate-300 group-hover/item:text-slate-500 transition-all"
        />
      </div>

      <div v-if="inboxes.length === 0" class="py-20 text-center">
        <div
          class="w-16 h-16 rounded-2xl bg-slate-50 flex items-center justify-center mx-auto mb-4 border border-slate-200"
        >
          <span class="i-lucide-message-square text-slate-300 text-3xl" />
        </div>
        <p class="text-xs font-medium text-slate-400">
          Aucune inbox disponible
        </p>
      </div>
    </div>

    <!-- Liste des Contacts (après choix Inbox) -->
    <div v-else-if="!selectedContact" class="flex-1 flex flex-col">
      <div
        v-if="contactsLoading"
        class="flex-1 flex items-center justify-center"
      >
        <div
          class="w-10 h-10 border-2 border-slate-100 border-t-emerald-500 rounded-full animate-spin"
        />
      </div>
      <div v-else class="flex-1 overflow-auto custom-scrollbar pr-1 space-y-2">
        <div
          v-for="contact in contacts"
          :key="contact.id"
          class="group/item flex items-center gap-3 p-3 transition-all duration-300 rounded-xl border border-slate-100 bg-slate-50 hover:bg-white hover:border-slate-200 cursor-pointer"
          @click="selectContact(contact)"
        >
          <div class="relative flex-shrink-0">
            <div
              class="h-10 w-10 rounded-lg border border-slate-200 bg-white overflow-hidden"
            >
              <img
                v-if="contact.avatar_url"
                :src="contact.avatar_url"
                :alt="contact.name"
                class="w-full h-full object-cover"
              />
              <div
                v-else
                class="w-full h-full flex items-center justify-center bg-slate-100"
              >
                <span class="i-lucide-user h-5 w-5 text-slate-400" />
              </div>
            </div>
          </div>
          <div class="flex-1 min-w-0">
            <div class="text-sm font-semibold truncate text-slate-900">
              {{ contact.name }}
            </div>
            <div class="text-xs font-medium text-slate-400 truncate">
              {{
                contact.email || contact.phone_number || "Pas d'info de contact"
              }}
            </div>
          </div>
        </div>
        <div
          v-if="contacts.length === 0"
          class="py-12 text-center text-slate-400 text-xs"
        >
          Aucun contact trouvé pour cette inbox.
        </div>
      </div>
    </div>

    <!-- Vue Messagerie pour le Contact sélectionné -->
    <div v-else class="flex-1 overflow-hidden flex flex-col">
      <!-- Mode Nouveau Message -->
      <div v-if="isNewMessageMode" class="flex-1 flex flex-col">
        <div
          class="flex-1 bg-slate-50 rounded-2xl p-6 border border-slate-200 flex flex-col"
        >
          <div class="mb-4">
            <h4
              class="text-xs font-bold text-slate-700 uppercase tracking-wider mb-2"
            >
              Nouveau message
            </h4>
            <p class="text-xs text-slate-500 font-medium">
              Envoyez un message pour démarrer une nouvelle conversation.
            </p>
          </div>

          <textarea
            v-model="newMessageContent"
            class="flex-1 w-full p-4 rounded-xl bg-white border border-slate-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 resize-none text-sm font-medium text-slate-700 placeholder:text-slate-400 custom-scrollbar transition-all"
            placeholder="Écrivez votre message ici..."
          />

          <div class="mt-4 flex items-center justify-end gap-3">
            <button
              class="px-6 py-2.5 rounded-xl text-[10px] font-semibold text-slate-500 hover:text-slate-700 transition-colors"
              @click="cancelNewMessage"
            >
              Annuler
            </button>
            <button
              class="px-6 py-2.5 rounded-xl bg-emerald-500 text-white text-[10px] font-semibold hover:bg-emerald-600 transition-all shadow-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              :disabled="!newMessageContent.trim() || isCreatingConversation"
              @click="createConversationAndSendMessage"
            >
              <span
                v-if="isCreatingConversation"
                class="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin"
              />
              Envoyer
            </button>
          </div>
        </div>
      </div>

      <!-- Liste des conversations existantes -->
      <ConversationsListWidget
        v-else
        :widget="{
          type: 'conversations_list',
          props: {
            title: '',
            apiConnector: {
              endpointId: 'conversations_list',
              params: {
                inbox_id: selectedInbox.id,
                contact_id: selectedContact.id,
                status: 'all',
              },
            },
          },
        }"
      />
    </div>
  </div>
</template>
